import type { Key } from 'react';

// Shared column sorter/filter builders for the admin panel's tables -- every
// section wires antd's own `sorter`/`filters`+`onFilter` the same way, so
// this avoids repeating the comparator boilerplate in each section file.

export const textSorter =
  <T extends object>(key: keyof T) =>
  (a: T, b: T): number =>
    String(a[key] ?? '').localeCompare(String(b[key] ?? ''));

export const numberSorter =
  <T extends object>(key: keyof T) =>
  (a: T, b: T): number =>
    (Number(a[key]) || 0) - (Number(b[key]) || 0);

export const dateSorter =
  <T extends object>(key: keyof T) =>
  (a: T, b: T): number =>
    new Date(String(a[key])).getTime() - new Date(String(b[key])).getTime();

/** Builds antd `filters` entries from a fixed list of enum-like values, labeled via the given lookup. */
export const enumColumnFilters = (values: string[], labelFor: (value: string) => string) =>
  values.map((value) => ({ text: labelFor(value), value }));

/** Builds antd `filters` entries for a boolean column; pair with booleanOnFilter for the matching key. */
export const booleanColumnFilters = (trueLabel: string, falseLabel: string) => [
  { text: trueLabel, value: 'true' },
  { text: falseLabel, value: 'false' },
];

export const booleanOnFilter =
  <T extends object>(key: keyof T) =>
  (value: boolean | Key, record: T): boolean =>
    Boolean(record[key]) === (value === 'true');

/** Builds antd `filters` entries from whatever values are actually present in the loaded rows -- for columns with no fixed enum (e.g. free-form action/admin names). */
export const uniqueValueFilters = <T extends object>(data: T[], key: keyof T) =>
  Array.from(new Set(data.map((row) => String(row[key] ?? ''))))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ text: value, value }));

export const stringOnFilter =
  <T extends object>(key: keyof T) =>
  (value: boolean | Key, record: T): boolean =>
    String(record[key] ?? '') === value;
